import * as React from "react";
import { Trash2, Loader2, Clock } from "lucide-react";
import { Modal } from "@/components/Modal.js";
import { SearchableSelect } from "@/components/SearchableSelect.js";
import { TagInput } from "@/components/TagInput.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { PhotoGallery } from "@/components/PhotoGallery.js";
import { useToast } from "@/components/Toast.js";
import { apiFetch } from "@/lib/api.js";
import { cn } from "@/lib/utils.js";
import { useSettings } from "@/contexts/useSettings.js";
import type { Space } from "@/hooks/useSpaces.js";

interface Item {
  id: string; name: string; description: string | null; quantity: number; status: string | null;
  manufacturer: string | null; purchaseDate: string | null; careInstructions: string | null; lastUsedDate: string | null;
  spaceId: string | null; spaceName: string | null;
  tags: string[]; photoUrl: string | null; notes: string | null; createdAt: string; updatedAt: string;
  estimatedValue: number | null;
  relocateAt?: string | null;
  /** Planned move (decided, not yet moved). */
  plannedSpaceName?: string | null;
}

interface Props {
  item: Item | null;
  onClose: () => void;
  spaces: Space[];
  onUpdated: () => void;
  onSpaceCreated?: () => void;
}

export function ItemDetailModal({ item, onClose, spaces, onUpdated, onSpaceCreated }: Props) {
  const toast = useToast();
  const { quickAddFields } = useSettings();
  const [name, setName] = React.useState("");
  const [spaceId, setSpaceId] = React.useState<string | null>(null);
  const [quantity, setQuantity] = React.useState(1);
  const [description, setDescription] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);
  const [status, setStatus] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [manufacturer, setManufacturer] = React.useState("");
  const [purchaseDate, setPurchaseDate] = React.useState("");
  const [careInstructions, setCareInstructions] = React.useState("");
  const [lastUsedDate, setLastUsedDate] = React.useState("");
  const [estimatedValue, setEstimatedValue] = React.useState("");
  const [relocateFlagged, setRelocateFlagged] = React.useState(false);
  const [relocateSaving, setRelocateSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [tab, setTab] = React.useState<"details" | "history">("details");
  const [history, setHistory] = React.useState<{ id: string; action: string; details: string | null; userName: string | null; createdAt: string }[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [photosChanged, setPhotosChanged] = React.useState(false);

  React.useEffect(() => {
    if (item) {
      setName(item.name); setSpaceId(item.spaceId); setQuantity(item.quantity);
      setDescription(item.description || ""); setTags(item.tags || []);
      setStatus(item.status || ""); setNotes(item.notes || "");
      setManufacturer(item.manufacturer || ""); setPurchaseDate(item.purchaseDate || "");
      setCareInstructions(item.careInstructions || ""); setLastUsedDate(item.lastUsedDate || "");
      setEstimatedValue(item.estimatedValue ? (item.estimatedValue / 100).toFixed(2) : "");
      setRelocateFlagged(Boolean(item.relocateAt));
      setError("");
      setTab("details"); setHistory([]);
      setPhotosChanged(false);
    }
  }, [item, quickAddFields]);

  React.useEffect(() => {
    if (tab === "history" && item && history.length === 0) {
      setHistoryLoading(true);
      apiFetch<typeof history>(`/api/items/${item.id}/history`).then(setHistory).catch(() => {}).finally(() => setHistoryLoading(false));
    }
  }, [tab, item]);

  const isDirty = item ? (
    name !== item.name || spaceId !== item.spaceId || quantity !== item.quantity ||
    description !== (item.description || "") || JSON.stringify(tags) !== JSON.stringify(item.tags || []) ||
    status !== (item.status || "") || notes !== (item.notes || "") ||
    manufacturer !== (item.manufacturer || "") || purchaseDate !== (item.purchaseDate || "") ||
    careInstructions !== (item.careInstructions || "") || lastUsedDate !== (item.lastUsedDate || "") ||
    estimatedValue !== (item.estimatedValue ? (item.estimatedValue / 100).toFixed(2) : "") ||
    photosChanged
  ) : false;

  const handleSave = async () => {
    if (!item || !name.trim()) return;
    setLoading(true); setError("");
    try {
      const body: Record<string, unknown> = { name: name.trim(), spaceId, quantity, description: description.trim() || null, tags };
      body.status = status || null;
      body.notes = notes.trim() || null;
      body.manufacturer = manufacturer.trim() || null;
      body.purchaseDate = purchaseDate || null;
      body.careInstructions = careInstructions.trim() || null;
      body.lastUsedDate = lastUsedDate || null;
      body.estimatedValue = estimatedValue ? Math.round(parseFloat(estimatedValue) * 100) : null;
      await apiFetch(`/api/items/${item.id}`, { method: "PUT", body: JSON.stringify(body) });
      toast.show("Item updated!");
      onUpdated(); onClose();
    } catch (e: any) { setError(e.message ?? "Failed to save"); }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!item) return;
    try {
      await apiFetch(`/api/items/${item.id}`, { method: "DELETE" });
      toast.show("Item deleted", "success", {
        label: "Undo",
        onClick: async () => {
          try {
            await apiFetch(`/api/items/${item.id}/restore`, { method: "POST" });
            toast.show("Item restored!");
            onUpdated();
          } catch { toast.show("Failed to restore", "error"); }
        },
      });
      onUpdated(); onClose();
    } catch (e: any) { toast.show(e.message ?? "Failed to delete", "error"); }
    setConfirmDelete(false);
  };

  // P0-B (R-B1/B6): flag "I want to move this" / dismiss ("it's fine where it
  // is"). Independent of location — the item keeps its current spot; this just
  // adds/removes it from the Re-locate list. Persists immediately (no Save needed).
  const handleToggleRelocate = async () => {
    if (!item || relocateSaving) return;
    const next = !relocateFlagged;
    setRelocateSaving(true);
    setRelocateFlagged(next);
    try {
      await apiFetch(`/api/items/${item.id}/relocate`, { method: "PUT", body: JSON.stringify({ relocate: next }) });
      toast.show(next ? "Flagged to move." : "Cleared — it's fine where it is.", "success");
      onUpdated();
    } catch (e: any) {
      setRelocateFlagged(!next);
      toast.show(e.message ?? "Failed to update", "error");
    }
    setRelocateSaving(false);
  };

  if (!item) return null;

  const fi = "w-full px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";

  return (
    <>
      <Modal open={!!item} onClose={onClose} title={undefined}>
        {error && <p className="text-[0.8125rem] text-error-text mb-3">{error}</p>}

        {/* Tabs */}
        <div className="flex gap-1 mb-3">
          <button onClick={() => setTab("details")} className={cn("px-4 py-2 rounded-md text-sm font-medium transition-colors", tab === "details" ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-clay-muted/70")}>Details</button>
          <button onClick={() => setTab("history")} className={cn("px-4 py-2 rounded-md text-sm font-medium transition-colors", tab === "history" ? "bg-primary text-white" : "bg-clay-muted text-body hover:bg-clay-muted/70")}><Clock className="w-3.5 h-3.5 inline mr-1" />History</button>
        </div>

        {tab === "history" ? (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {historyLoading ? <div className="h-20 bg-paper-raised rounded-lg animate-pulse" /> : history.length === 0 ? (
              <p className="text-sm text-faint text-center py-8">No activity yet</p>
            ) : history.map((h) => {
              const d = h.details ? JSON.parse(h.details) : {};
              const label = h.action === "moved" ? `Moved${d.from ? "" : " to a space"}` : h.action === "quantity_changed" ? `Quantity ${d.from} → ${d.to}` : h.action === "last_seen" ? `Marked as seen` : h.action === "created" ? "Created" : h.action === "deleted" ? "Deleted" : h.action === "restored" ? "Restored" : "Updated";
              return (
                <div key={h.id} className="flex items-start gap-3 p-2 rounded-md hover:bg-amethyst-soft/50 transition-colors">
                  <div className="w-2 h-2 rounded-pill bg-sage mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-body">{label}</p>
                    {h.userName && <p className="text-[0.8125rem] text-faint">by {h.userName}</p>}
                  </div>
                  <span className="text-[0.8125rem] text-faint shrink-0 tabular-nums">{new Date(h.createdAt).toLocaleDateString()}</span>
                </div>
              );
            })}
          </div>
        ) : (
        <div className="space-y-3">
          {/* Photo Gallery */}
          <PhotoGallery
            itemId={item.id}
            legacyPhotoUrl={item.photoUrl}
            editable
            onChanged={() => setPhotosChanged(true)}
          />

          {/* Actions - right under photo */}
          <div className="flex gap-2">
            {isDirty ? (
              <button onClick={handleSave} disabled={loading || !name.trim()} className="flex-1 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Save Changes"}
              </button>
            ) : (
              <div className="flex-1" />
            )}
            <button onClick={() => setConfirmDelete(true)} aria-label="Delete item" className="px-3 py-2 rounded-md bg-error-wash text-error-text hover:bg-error-wash/70 transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>

          {/* Editable fields */}
          <div>
            <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Item Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter item name" className={fi + " font-medium"} />
          </div>
          <div>
            <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Location</label>
            <SearchableSelect spaces={spaces} value={spaceId} onChange={setSpaceId} onSpaceCreated={onSpaceCreated} />
            {/* Planned move (§6b): the location above is where it is now. */}
            {item.plannedSpaceName && !relocateFlagged && (
              <p className="mt-1.5 text-[0.8125rem] text-amethyst-ink">Planned to move to {item.plannedSpaceName} — in Moves to make.</p>
            )}
          </div>

          {/* Re-locate designation (P0-B). A toggle row — keeps the item where it
              is, adds it to the Re-locate list. */}
          <button
            type="button"
            onClick={handleToggleRelocate}
            disabled={relocateSaving}
            aria-pressed={relocateFlagged}
            className={cn(
              "w-full flex items-center gap-3 p-3 rounded-md border text-left transition-colors disabled:cursor-not-allowed",
              relocateFlagged ? "border-amethyst-ink/30 bg-amethyst-soft" : "border-clay-border hover:border-primary/40"
            )}
          >
            <span className={cn(
              "shrink-0 w-9 h-5 rounded-pill relative transition-colors",
              relocateFlagged ? "bg-primary" : "bg-clay-muted"
            )}>
              <span className={cn(
                "absolute top-0.5 w-4 h-4 rounded-pill bg-white transition-all",
                relocateFlagged ? "left-[1.125rem]" : "left-0.5"
              )} />
            </span>
            <span className="flex-1 min-w-0">
              <span className={cn("block text-sm font-medium", relocateFlagged ? "text-amethyst-ink" : "text-ink")}>
                {relocateFlagged ? "Flagged to move" : "Flag to move"}
              </span>
              <span className="block text-[0.8125rem] text-faint">Keeps it where it is; adds it to your Re-locate list.</span>
            </span>
            {relocateSaving && <Loader2 className="w-4 h-4 animate-spin text-faint shrink-0" />}
          </button>
          <div className="flex gap-2 items-end">
            <div>
              <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Quantity</label>
              <input type="number" min={0} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} className={fi + " w-20"} />
            </div>
          </div>
          <div>
            <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Description <span className="text-faint normal-case font-normal">(optional)</span></label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Add more details..." rows={2} className={fi + " resize-none"} />
          </div>
          <div>
            <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Tags</label>
            <TagInput tags={tags} onChange={setTags} />
          </div>

          {/* Optional fields */}
          {quickAddFields.includes("status") && (
            <div>
              <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Status</label>
              <input placeholder="e.g. in use, stored, lent out" value={status} onChange={(e) => setStatus(e.target.value)} className={fi} />
            </div>
          )}
          {quickAddFields.includes("notes") && (
            <div>
              <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Notes</label>
              <textarea placeholder="Additional notes..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={fi + " resize-none"} />
            </div>
          )}
          {quickAddFields.includes("manufacturer") && (
            <div>
              <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Manufacturer</label>
              <input placeholder="Brand or maker" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} className={fi} />
            </div>
          )}
          {quickAddFields.includes("purchaseDate") && (
            <div>
              <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Purchase Date</label>
              <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className={fi} />
            </div>
          )}
          {quickAddFields.includes("careInstructions") && (
            <div>
              <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Care Instructions</label>
              <textarea placeholder="Cleaning or maintenance tips..." value={careInstructions} onChange={(e) => setCareInstructions(e.target.value)} rows={2} className={fi + " resize-none"} />
            </div>
          )}
          {quickAddFields.includes("lastUsedDate") && (
            <div>
              <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Last Used Date</label>
              <input type="date" value={lastUsedDate} onChange={(e) => setLastUsedDate(e.target.value)} className={fi} />
            </div>
          )}
          {quickAddFields.includes("estimatedValue") && (
            <div>
              <label className="text-[0.8125rem] font-semibold text-faint uppercase tracking-wide mb-1 block">Estimated Value ($)</label>
              <input type="number" min={0} step="0.01" placeholder="0.00" value={estimatedValue} onChange={(e) => setEstimatedValue(e.target.value)} className={fi} />
            </div>
          )}

          <p className="text-[0.8125rem] text-faint tabular-nums">Added {new Date(item.createdAt).toLocaleDateString()}</p>
        </div>
        )}
      </Modal>
      <ConfirmDialog open={confirmDelete} title="Delete Item" message={`Delete "${item.name}"?`} confirmLabel="Delete" destructive onConfirm={handleDelete} onCancel={() => setConfirmDelete(false)} />
    </>
  );
}
